import { useEffect, useRef } from 'react';
import { usePathname } from 'expo-router';
import type { AnalyticsScalar } from '../lib/analytics';
import { trackAppScreenReady } from '../lib/product-events';

const navigationStartedAt = new Map<string, number>();

function routeVariants(route: string): string[] {
  const withoutGroup = route.replace('/(app)', '') || '/';
  return route === withoutGroup ? [route] : [route, withoutGroup];
}

export function markScreenNavigationStarted(route: string): void {
  const now = Date.now();
  for (const variant of routeVariants(route)) {
    navigationStartedAt.set(variant, now);
  }
}

export function useScreenReadyTelemetry(params: {
  ready: boolean;
  screenName: string;
  metadata?: Record<string, AnalyticsScalar>;
}): void {
  const pathname = usePathname();
  const mountedAtRef = useRef(Date.now());
  const reportedRef = useRef(false);

  useEffect(() => {
    if (!params.ready || reportedRef.current) return;
    reportedRef.current = true;

    const route = pathname || params.screenName;
    const startedAt = routeVariants(route)
      .map((variant) => navigationStartedAt.get(variant))
      .find((value): value is number => value != null)
      ?? mountedAtRef.current;

    for (const variant of routeVariants(route)) {
      navigationStartedAt.delete(variant);
    }

    trackAppScreenReady({
      route,
      screenName: params.screenName,
      durationMs: Math.max(0, Date.now() - startedAt),
      metadata: params.metadata,
    });
  }, [params.metadata, params.ready, params.screenName, pathname]);
}
